import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input } from 'antd'
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form'
import { WarningModal } from '@User/MyAccountPage/components'
import pageStyles from '@User/MyAccountPage/styles.css'

import { useUpdateUserProfileMutation } from '~/gql/graphql'
import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import { IdentityType } from '~/redux-modules/identity/type'
import { hasErrors } from '~/lib/formHelpers'
import { handleSubmit } from '../logic'
import { FIRST_NAME, LAST_NAME, NAME_RULES } from '../constants'

const PLACEMENT = 'user-profile-name-form'

interface NameFormPropsType extends FormComponentProps {
    identity: IdentityType
    warning: boolean
    onCancel: () => void
    setCurrentForm: Function
}

function NameForm(props: NameFormPropsType) {
    const {
        identity,
        onCancel,
        form,
        form: {
            getFieldDecorator,
            getFieldsError,
            getFieldError,
            isFieldTouched,
        },
        warning,
        setCurrentForm,
    } = props
    const [firstTimeRender, setFirstTimeRender] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [updateUserProfile] = useUpdateUserProfileMutation()
    const dispatch = useDispatch()
    // Only show error after a field is touched.
    const firstNameError =
        isFieldTouched(FIRST_NAME) && getFieldError(FIRST_NAME)
    const lastNameError = isFieldTouched(LAST_NAME) && getFieldError(LAST_NAME)

    useEffect(() => {
        if (firstTimeRender) {
            setCurrentForm(form)
            setFirstTimeRender(false)
        }
    }, [firstTimeRender, form, setCurrentForm])

    const handleFormSubmit = () =>
        handleSubmit({
            form,
            setLoading,
            identity,
            dispatch,
            onCancel,
            updateUserProfile,
        })

    return (
        <Box className={pageStyles.mobileFormBox}>
            {warning && (
                <WarningModal
                    visible={warning}
                    onClose={onCancel}
                    onConfirm={handleFormSubmit}
                    loading={loading}
                />
            )}
            <Box
                width="60%"
                className={`${pageStyles.mobileLargeWidth} ${pageStyles.mobileColBox}`}
            >
                <Box
                    direction="col"
                    marginRight="4px"
                    width="50%"
                    className={pageStyles.mobileLargeWidth}
                >
                    <Text>First</Text>
                    <Form.Item
                        required
                        style={{ marginBottom: '0' }}
                        validateStatus={firstNameError ? 'error' : ''}
                        help={firstNameError || ''}
                    >
                        {getFieldDecorator(FIRST_NAME, {
                            rules: NAME_RULES,
                            validateTrigger: 'onBlur',
                            initialValue: identity?.firstName || '',
                        })(<Input data-test-target={FIRST_NAME} />)}
                    </Form.Item>
                </Box>
                <Box
                    width="50%"
                    className={pageStyles.mobileLargeWidth}
                    direction="col"
                >
                    <Text>Last</Text>
                    <Form.Item
                        required
                        style={{ marginBottom: '0' }}
                        validateStatus={lastNameError ? 'error' : ''}
                        help={lastNameError || ''}
                    >
                        {getFieldDecorator(LAST_NAME, {
                            rules: NAME_RULES,
                            validateTrigger: 'onBlur',
                            initialValue: identity?.lastName || '',
                        })(<Input data-test-target={LAST_NAME} />)}
                    </Form.Item>
                </Box>
            </Box>
            <Box className={pageStyles.mobileButtonBox} marginTop="16px">
                <Button
                    type="primary"
                    name="save"
                    className={pageStyles.mobileRightButton}
                    disabled={loading || hasErrors(getFieldsError())}
                    loading={loading}
                    placement={PLACEMENT}
                    data-test-target="user-profile-save-name"
                    onClick={handleFormSubmit}
                >
                    Save
                </Button>
                <Button
                    className={pageStyles.mobileLeftButton}
                    name="cancel"
                    placement={PLACEMENT}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    )
}

export default Form.create<NameFormPropsType>()(NameForm)
